import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Box,
  Typography
} from '@mui/material';
import { Science as ScienceIcon } from '@mui/icons-material';
import { generateTestAttendees } from '../utils/testDataGenerator';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';

interface TestDataGeneratorProps {
  eventId: string;
  disabled?: boolean;
  onDataGenerated?: () => void;
}

const TestDataGenerator: React.FC<TestDataGeneratorProps> = ({ eventId, disabled, onDataGenerated }) => {
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(50);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleOpen = () => {
    setOpen(true);
    setError(null);
    setSuccess(false);
  };

  const handleClose = () => {
    setOpen(false);
    setError(null);
    setSuccess(false);
  };

  const handleGenerate = async () => {
    try {
       setLoading(true);
       setError(null);
       setSuccess(false);

       // Fetch event data first
       const eventRef = doc(db, 'events', eventId);
       const eventSnapshot = await getDoc(eventRef);

       if (!eventSnapshot.exists()) {
          throw new Error('Event not found');
       }

       const eventData = eventSnapshot.data();

       if (!eventData.ticketTypes || eventData.ticketTypes.length === 0) {
          throw new Error('Event has no ticket types defined. Please add ticket types first.');
       }

       // Convert dates if they're Firestore timestamps
       const startDate = eventData.startDate.toDate
          ? eventData.startDate.toDate()
          : new Date(eventData.startDate);
       const endDate = eventData.endDate.toDate
          ? eventData.endDate.toDate()
          : new Date(eventData.endDate);

       await generateTestAttendees({
          count,
          eventId,
          startDate,
          endDate,
          ticketTypes: eventData.ticketTypes,
       });

       setSuccess(true);

       if (onDataGenerated) {
          onDataGenerated();
       }

       setTimeout(() => {
          handleClose();
       }, 1500);
    } catch (err) {
      console.error('Error generating test data:', err);
      setError(err instanceof Error ? err.message : 'Failed to generate test data');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<ScienceIcon />}
        onClick={handleOpen}
        disabled={disabled}
        sx={{ ml: 1 }}
      >
        Generate Test Data
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Generate Test Attendees</DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Test attendees generated successfully!
            </Alert>
          )}
          <Box sx={{ mb: 2, mt: 2 }}>
            <Typography variant="body2" color="text.secondary" paragraph>
              This will generate random test attendees with realistic names and emails,
              distributed across available ticket types. The attendees will have random
              purchase dates and check-in times.
            </Typography>
          </Box>
          <TextField
            fullWidth
            label="Number of Attendees to Generate"
            type="number"
            value={count}
            onChange={(e) => setCount(Math.max(1, Math.min(500, parseInt(e.target.value) || 0)))}
            inputProps={{ min: 1, max: 500 }}
            helperText="Maximum 500 attendees per generation"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={handleGenerate} 
            variant="contained" 
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? 'Generating...' : 'Generate'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TestDataGenerator; 